import React, { useMemo } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { FormattedDate } from 'react-intl';
import messages from './messages';
import type { Prospect } from '../../ProspectProfile/types';
import { Col, Row } from 'react-bootstrap';
import type { ContactType, PetType, ReferralType } from '../../App/types';
import ElementWithPermissions from '../../../components/ElementWithPermissions';
import { pathOr } from 'ramda';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { formatNBedsArr } from '../../../utils/prospectPreferences-helpers';
import { getFloorPlanName } from '../../../utils/floorPlan';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import { getProspectTabMoveInDates } from '../../ManageProspects/utils';
import {
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { formatDate } from '../../../utils/formatter';

type Props = {
  intl: Object,
  prospect: Prospect,
  petTypes: Array<PetType>,
  contactTypes: Array<ContactType>,
  referralTypes: Array<ReferralType>,
  handleProspectInfoEdit?: Function,
  floorPlans: string[],
  isWaitlistV2Enabled?: boolean,
};

type OverViewProps = {
  intl: Object,
  prospect: Prospect,
  contactTypes: Array<ContactType>,
  referralTypes: Array<ReferralType>,
  handleProspectInfoEdit?: Function,
  floorPlans?: string[],
  isWaitlistV2Enabled?: boolean,
};

const dashesIfNullOrUndefined = (data: string | number) =>
  !data || data === null ? '---' : data;

// FormattedDate parses a date-only string as UTC midnight, which renders the
// previous day west of UTC. Hand it local midnight of the stored day instead.
const toLocalDay = (date: string | Object) =>
  moment(formatDateDB(date, false)).toDate();

export const Overview = ({
  intl,
  prospect,
  contactTypes,
  referralTypes,
  handleProspectInfoEdit,
  floorPlans,
  isWaitlistV2Enabled = false,
}: OverViewProps) => {
  const {
    propertySettingApplyWithoutAUnit,
    accessibleUnit,
    affordableNonAffordableProspect,
  } = useFlags();

  const { prospectPreferences } = prospect;
  const emptyContactOrReferral = {
    id: 'default',
    name: '---',
    translations: { en: '---', es: '---' },
  };
  // $FlowFixMe
  const preferences = pathOr({}, ['prospectPreferences'], prospect);
  const contactType =
    _.find(contactTypes, ['id', prospect.contactTypeId]) ||
    emptyContactOrReferral;
  const referralType =
    _.find(referralTypes, ['id', prospect.referralTypeId]) ||
    emptyContactOrReferral;
  const { floorPlan, nBeds, nBedsArr } = useMemo(() => {
    return {
      nBeds: dashesIfNullOrUndefined(prospectPreferences.nBeds),
      nBedsArr: formatNBedsArr(
        prospectPreferences?.nBedsArr,
        prospectPreferences?.nBeds,
      ),
      floorPlan: getFloorPlanName({
        floorPlans,
        preferredFloorPlanId: prospectPreferences.preferredFloorPlanId,
      }),
    };
  }, [
    floorPlans,
    prospectPreferences.nBeds,
    prospectPreferences?.nBedsArr,
    prospectPreferences.preferredFloorPlanId,
  ]);
  const nBaths = dashesIfNullOrUndefined(pathOr('', ['nBaths'], preferences));
  const nHalfBaths = dashesIfNullOrUndefined(
    pathOr('', ['nHalfBaths'], preferences),
  );
  const helpText = dashesIfNullOrUndefined(
    pathOr('', ['helpText'], preferences),
  );
  const wants = dashesIfNullOrUndefined(pathOr('', ['wants'], preferences));
  const notWants = dashesIfNullOrUndefined(
    pathOr('', ['notWants'], preferences),
  );
  const { assigningUnitsMoveInDates: assigningUnitsMoveInDatesFlag } =
    useFlags();
  const { moveInDateFrom, moveInDateTo } = getProspectTabMoveInDates({
    prospectPreferences: prospect?.prospectPreferences,
    isWaitlistV2Enabled,
  });

  const unitType = dashesIfNullOrUndefined(
    pathOr('', ['unitType'], preferences),
  );

  const affordableProgramsArr = dashesIfNullOrUndefined(
    pathOr('', ['affordableProgramsArr'], preferences),
  );

  return (
    <div>
      <Col xs={12} sm={4}>
        <h1>{intl.formatMessage(messages.overview)}</h1>
      </Col>
      <Col xs={12} sm={8}>
        <div className="btn-toolbar">
          {handleProspectInfoEdit ? (
            <ElementWithPermissions scope={['prospect-update']}>
              <button
                className="btn btn-primary--ghost pull-right"
                onClick={handleProspectInfoEdit}
              >
                <i className="et-pencil icon" />
                {intl.formatMessage(messages.editInfo)}
              </button>
            </ElementWithPermissions>
          ) : (
            ''
          )}
        </div>
      </Col>
      <div className="divider" />
      <Col xs={12}>
        <h3>{intl.formatMessage(messages.introductoryNote)}</h3>
        <p>
          {helpText
            .toString()
            .split('\n')
            .map((line, i) => (
              <span key={i}>
                {line}
                <br />
              </span>
            ))}
        </p>
      </Col>
      <div className="divider" />
      <Col xs={12}>
        <h3>{intl.formatMessage(messages.details)}</h3>
        <section id="section-details">
          <Row>
            <Col xs={12} sm={3} className="padbottom10">
              <label>{intl.formatMessage(messages.noBeds)}</label>
              <p>{propertySettingApplyWithoutAUnit ? nBedsArr : nBeds}</p>
            </Col>
            <Col xs={12} sm={3} className="padbottom10">
              <label>{intl.formatMessage(messages.noBaths)}</label>
              <p>{nBaths}</p>
            </Col>
            <Col xs={12} sm={3} className="padbottom10">
              <label># Half Bath</label>
              <p>{nHalfBaths}</p>
            </Col>
            <Col xs={12} sm={3}>
              <label>{intl.formatMessage(messages.moveInDate)}</label>
              {assigningUnitsMoveInDatesFlag ? (
                <p>
                  {moveInDateFrom ? (
                    <FormattedDate
                      value={toLocalDay(moveInDateFrom)}
                      year="numeric"
                      month="2-digit"
                      day="2-digit"
                    />
                  ) : (
                    intl.formatMessage(messages.noMoveInDate)
                  )}
                  {moveInDateFrom && moveInDateTo ? (
                    <span>
                      {' '}
                      -{' '}
                      <FormattedDate
                        value={toLocalDay(moveInDateTo)}
                        year="numeric"
                        month="2-digit"
                        day="2-digit"
                      />{' '}
                    </span>
                  ) : (
                    ''
                  )}
                </p>
              ) : (
                <p>
                  {preferences.moveInDateFrom ? (
                    <FormattedDate
                      value={toLocalDay(preferences.moveInDateFrom)}
                      year="numeric"
                      month="2-digit"
                      day="2-digit"
                    />
                  ) : (
                    intl.formatMessage(messages.noMoveInDate)
                  )}
                  {preferences.moveInDateTo && preferences.moveInDateFrom ? (
                    <span>
                      {' '}
                      -{' '}
                      <FormattedDate
                        value={toLocalDay(preferences.moveInDateTo)}
                        year="numeric"
                        month="2-digit"
                        day="2-digit"
                      />{' '}
                    </span>
                  ) : (
                    ''
                  )}
                </p>
              )}
            </Col>
          </Row>
          <Row>
            {propertySettingApplyWithoutAUnit && (
              <Col xs={12} sm={3}>
                <label>{intl.formatMessage(messages.floorPlan)}</label>
                <p>{floorPlan}</p>
              </Col>
            )}
            <Col xs={12} sm={3}>
              <label>{intl.formatMessage(messages.priceRange)}</label>
              <p>
                {preferences.priceFrom ? `$${preferences.priceFrom}` : '?'} -{' '}
                {preferences.priceTo ? `$${preferences.priceTo}` : '?'}
              </p>
            </Col>
            <Col xs={12} sm={3}>
              <label>{intl.formatMessage(messages.leaseTerm)}</label>
              <p>
                {preferences.leaseTermInMonths
                  ? `${preferences.leaseTermInMonths} months`
                  : '---'}
              </p>
            </Col>
            {accessibleUnit && (
              <Col xs={12} sm={3}>
                <label>{intl.formatMessage(messages.accessibleUnit)}</label>
                <p>{preferences.prefersUnitAccessible ? 'Yes' : 'No'}</p>
              </Col>
            )}
            {affordableNonAffordableProspect && (
              <>
                <Col xs={12} sm={3}>
                  <label>{intl.formatMessage(messages.unitType)}</label>
                  <p>{unitType.charAt(0).toUpperCase() + unitType.slice(1)}</p>
                </Col>
                {affordableProgramsArr?.length ? (
                  <Col xs={12} sm={3}>
                    <label>
                      {intl.formatMessage(messages.affordablePrograms)}
                    </label>
                    <p>
                      {affordableProgramsArr !== '---'
                        ? affordableProgramsArr?.join(', ')
                        : affordableProgramsArr}
                    </p>
                  </Col>
                ) : (
                  <></>
                )}
              </>
            )}
          </Row>
          <div className="divider" />
          <Col xs={12}>
            <h3>{intl.formatMessage(messages.wants)}</h3>
            <p>
              {wants
                .toString()
                .split('\n')
                .map((line, i) => (
                  <span key={i}>
                    {line}
                    <br />
                  </span>
                ))}
            </p>
          </Col>
          <div className="divider" />
          <Col xs={12}>
            <h3>{intl.formatMessage(messages.doesntWant)}</h3>
            <p>
              {notWants
                .toString()
                .split('\n')
                .map((line, i) => (
                  <span key={i}>
                    {line}
                    <br />
                  </span>
                ))}
            </p>
          </Col>
          <div className="divider" />
          <Col xs={12}>
            <h3>{intl.formatMessage(messages.contactType)}</h3>
            <section id="contacttype">
              <p>{contactType.name}</p>
            </section>
          </Col>
          <Col xs={12}>
            <h3>{intl.formatMessage(messages.source)}</h3>
            {prospect?.contact?.leadSources &&
            Array.isArray(prospect.contact.leadSources) &&
            prospect.contact.leadSources.length > 0 ? (
              <List spacing={2}>
                {prospect.contact.leadSources.map((leadSource, index) => {
                  const leadSourceReferralType =
                    _.find(referralTypes, ['id', leadSource.referralTypeId]) ||
                    emptyContactOrReferral;
                  return (
                    <ListItem
                      key={index}
                      sx={{
                        width: 400,
                        padding: 2,
                        marginTop: index === 0 ? 0 : 2,
                        borderRadius: 'var(--Corner-Radius-4, 4px)',
                        border:
                          '1px solid var(--Color-Sys-primary-variant-light, #98BEFF)',
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="titleMedium">
                            {leadSourceReferralType.name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="bodyMedium">
                            <span style={{ marginRight: '20px' }}>
                              {formatDate(leadSource.createdAt, 'MM/DD/YYYY')}
                            </span>
                            {formatDate(leadSource.createdAt, 'hh:mm A')}
                          </Typography>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <section id="source">
                <p>{referralType.name}</p>
              </section>
            )}
          </Col>
        </section>
      </Col>
    </div>
  );
};

const ProspectInfoTab = ({
  intl,
  prospect,
  petTypes,
  contactTypes,
  referralTypes,
  handleProspectInfoEdit,
  floorPlans,
  scheduledMoveInDate,
  isWaitlistV2Enabled,
}: Props) => {
  return (
    <div className="tab-pane active" id="1a">
      <Overview
        intl={intl}
        prospect={prospect}
        contactTypes={contactTypes}
        referralTypes={referralTypes}
        handleProspectInfoEdit={handleProspectInfoEdit}
        floorPlans={floorPlans}
        isWaitlistV2Enabled={isWaitlistV2Enabled}
      />
    </div>
  );
};

export default ProspectInfoTab;
